/**
 * jCal — the JSON representation of iCalendar ([RFC 7265](https://www.rfc-editor.org/rfc/rfc7265.html)).
 *
 * Same event, same properties, JSON structure: `["vcalendar", [properties], [components]]`,
 * where each property is `[name, parameters, valueType, value]`. That shape is what makes it
 * worth shipping — an API consumer gets the full iCalendar semantics (zones, recurrence rules,
 * alarms) without writing a line-folding parser, which JSON-LD's flattened `Event` can't express.
 *
 * Generated from the draft, not by re-parsing our own `.ics`: a converter would inherit every
 * escaping decision from the text form, and jCal values are explicitly *unescaped* (JSON does the
 * quoting). Both writers read the same model, so they can't drift on what an event *is* — only
 * on how it's spelled, which is the point of the format registry.
 */

import {
  type EventDraft,
  type EventPerson,
  type EventRecurrence,
  type EventTime,
  effectiveEnd,
  timeClock,
  timeDate,
} from './model';
import { LIMITS, sanitizeEmail, sanitizeLine, sanitizeText, sanitizeUrl } from './sanitize';

/** A jCal parameter set: lowercase names, string (or string-list) values. */
export type JCalParams = Record<string, string | string[]>;

/** `[name, parameters, valueType, value]` — RFC 7265 §3.4. */
export type JCalProperty = [string, JCalParams, string, unknown];

/** `[name, [properties], [sub-components]]` — RFC 7265 §3.3. */
export type JCalComponent = [string, JCalProperty[], JCalComponent[]];

export interface JCalOptions {
  readonly uid?: string;
  /** iCalendar UTC stamp (`20260815T120000Z`) — the same one `toIcs` takes. */
  readonly dtstamp: string;
  readonly productId?: string;
  readonly method?: 'PUBLISH' | 'REQUEST';
}

const DEFAULT_PRODUCT_ID = '-//project//event-format//EN';

const text = (name: string, value: string): JCalProperty => [name, {}, 'text', value];

/**
 * jCal writes date-times in the ISO form (`2026-08-15T09:00:00`), not iCalendar's compact one,
 * and keeps the zone in a `tzid` parameter rather than in the value.
 */
function timeProperty(
  name: 'dtstart' | 'dtend',
  time: EventTime,
  exclusiveEndDay: boolean,
): JCalProperty {
  if (time.kind === 'date') {
    const date = exclusiveEndDay ? addDays(time.date, 1) : time.date;
    return [name, {}, 'date', date];
  }
  const value = `${timeDate(time)}T${timeClock(time) ?? '00:00:00'}`;
  if (time.timeZone === undefined) return [name, {}, 'date-time', value];
  if (time.timeZone === 'UTC') return [name, {}, 'date-time', `${value}Z`];
  const tzid = sanitizeLine(time.timeZone, 100);
  return tzid === '' ? [name, {}, 'date-time', value] : [name, { tzid }, 'date-time', value];
}

function addDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** `20260815T120000Z` → `2026-08-15T12:00:00Z`; anything malformed falls back like `toIcs`. */
function isoStamp(dtstamp: string): string {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(dtstamp);
  if (match === null) return '1970-01-01T00:00:00Z';
  const [, y, mo, d, h, mi, s] = match;
  return `${y}-${mo}-${d}T${h}:${mi}:${s}Z`;
}

/** A `recur` value is an object, not the `FREQ=…;COUNT=…` string the text form uses. */
function recurrenceValue(recurrence: EventRecurrence): Record<string, unknown> {
  const { frequency, interval, count, until } = recurrence;
  return {
    freq: frequency.toUpperCase(),
    ...(interval !== undefined && interval !== 1 ? { interval } : {}),
    ...(count !== undefined ? { count } : {}),
    ...(until !== undefined ? { until: `${until}T23:59:59Z` } : {}),
  };
}

/** A person, or `null` when the address can't be trusted — same rule as the `.ics` writer. */
function personProperty(name: 'organizer' | 'attendee', person: EventPerson): JCalProperty | null {
  const email = sanitizeEmail(person.email);
  if (email === null) return null;
  const cn = person.name === undefined ? undefined : sanitizeLine(person.name, LIMITS.personName);
  const params: JCalParams = {
    ...(cn === undefined || cn === '' ? {} : { cn }),
    ...(name === 'attendee'
      ? { role: 'REQ-PARTICIPANT', partstat: 'NEEDS-ACTION', rsvp: 'TRUE' }
      : {}),
  };
  return [name, params, 'cal-address', `mailto:${email}`];
}

function alarmComponent(minutesBefore: number): JCalComponent {
  return [
    'valarm',
    [
      text('action', 'DISPLAY'),
      text('description', 'Reminder'),
      ['trigger', {}, 'duration', `-PT${minutesBefore}M`],
    ],
    [],
  ];
}

function veventComponent(draft: EventDraft, options: JCalOptions): JCalComponent {
  const rawUid = options.uid ?? draft.uid;
  if (rawUid === undefined) throw new Error('toJcal needs a uid, on the draft or in options.');

  const properties: JCalProperty[] = [
    text('uid', sanitizeLine(rawUid, 200)),
    ['dtstamp', {}, 'date-time', isoStamp(options.dtstamp)],
    timeProperty('dtstart', draft.start, false),
    timeProperty('dtend', effectiveEnd(draft), draft.start.kind === 'date'),
    text('summary', sanitizeLine(draft.title, LIMITS.title)),
  ];

  if (draft.description !== undefined && draft.description !== '') {
    properties.push(text('description', sanitizeText(draft.description, LIMITS.description)));
  }
  if (draft.location !== undefined && draft.location !== '') {
    properties.push(text('location', sanitizeLine(draft.location, LIMITS.location)));
  }
  const url = draft.url === undefined ? null : sanitizeUrl(draft.url);
  if (url !== null) properties.push(['url', {}, 'uri', url]);
  if (draft.status !== undefined) properties.push(text('status', draft.status.toUpperCase()));
  if (draft.showAs !== undefined)
    properties.push(text('transp', draft.showAs === 'free' ? 'TRANSPARENT' : 'OPAQUE'));
  if (draft.recurrence !== undefined) {
    properties.push(['rrule', {}, 'recur', recurrenceValue(draft.recurrence)]);
  }
  if (draft.organizer !== undefined) {
    const organizer = personProperty('organizer', draft.organizer);
    if (organizer !== null) properties.push(organizer);
  }
  for (const attendee of (draft.attendees ?? []).slice(0, LIMITS.attendees)) {
    const property = personProperty('attendee', attendee);
    if (property !== null) properties.push(property);
  }

  const alarms = (draft.remindersMinutesBefore ?? [])
    .slice(0, LIMITS.reminders)
    .filter((minutes) => Number.isFinite(minutes) && minutes >= 0)
    .map((minutes) => alarmComponent(Math.trunc(minutes)));

  return ['vevent', properties, alarms];
}

function calendar(events: JCalComponent[], options: JCalOptions): JCalComponent {
  const properties: JCalProperty[] = [
    text('prodid', sanitizeLine(options.productId ?? DEFAULT_PRODUCT_ID, 200)),
    text('version', '2.0'),
    text('calscale', 'GREGORIAN'),
  ];
  if (options.method !== undefined) properties.push(text('method', options.method));
  return ['vcalendar', properties, events];
}

/** The jCal structure for one event. */
export function toJcal(draft: EventDraft, options: JCalOptions): JCalComponent {
  return calendar([veventComponent(draft, options)], options);
}

/** One jCal document holding several events — each draft supplies its own `uid`. */
export function toJcalCalendar(drafts: readonly EventDraft[], options: JCalOptions): JCalComponent {
  return calendar(
    drafts
      .slice(0, LIMITS.events)
      .map((draft) => veventComponent(draft, { ...options, uid: draft.uid ?? options.uid })),
    options,
  );
}

/** The `application/calendar+json` payload, pretty-printed. */
export function toJcalString(draft: EventDraft, options: JCalOptions): string {
  return JSON.stringify(toJcal(draft, options), null, 2);
}
